import { Map as immutableMap } from 'immutable';
import { getField, updateField } from 'vuex-map-fields';
import {
  ELEMENTS_TYPES,
  ELEMENTS_TYPES_BRANCH_ACTIONS_KEYS,
  ELEMENTS_TYPES_GROUPS,
} from '@/api/elements';
import objectValue from '@/utils/objectValue';

const STATE = immutableMap({
  importDate: Date.now(),
  showImport: false,
  importedJSON: null,
});

export const state = () => STATE.toJS();

export const getters = {
  getField,
};

export const mutations = {
  reset(state) {
    Object.assign(state, {
      ...STATE.toJS(),
      importDate: Date.now(),
    });
  },
  updateField,
};

export const actions = {
  async runImport(context) {
    const data = context.state.importedJSON;
    if (!data) {
      return false;
    }

    // Reset current nodes data.
    await context.dispatch('nodes/reset', null, {root: true});

    let dataJSON;

    try {
      dataJSON = JSON.parse(data);
    } catch (error) {
      console.warn('runImport', {error});
    }

    console.log('runImport', {dataJSON});
    if (!dataJSON) {
      return false;
    }

    // Update Greeting node.
    const nodeGreeting = await context.rootGetters['nodes/rootNode'];
    let firstNode = nodeGreeting;
    console.log('runImport', {nodeGreeting});

    if (!nodeGreeting) {
      return false;
    }

    const newNodeGreeting = dataJSON[ELEMENTS_TYPES.GREETING];
    if (newNodeGreeting) {
      await context.dispatch('nodes/updateNode', {
        node: {
          ...nodeGreeting,
          text: objectValue(newNodeGreeting, 'message.text', null),
        },
      }, {root: true});
    }

    if (newNodeGreeting.interactive_pause) {
      firstNode = await context.dispatch('nodes/addNode', {
        isNew: true,
        from: firstNode.id,
        type: ELEMENTS_TYPES.SMART_PAUSE,
      }, {root: true});
    }

    if (dataJSON.scenario) {
      const scenarioArray = Object.values(dataJSON.scenario);

      const processedScenarioObject = {};
      const processScenario = async ({ index, from }) => {
        const scenarioItem = scenarioArray[index];
        console.log('runImport - processScenario', {index, scenarioItem});

        const newScenarioNode = await context.dispatch('nodes/addNode', {
          isNew: true,
          from,
          type: ELEMENTS_TYPES.MESSAGE,
          text: objectValue(scenarioItem, 'message.text', null),
        }, {root: true});
        console.log('runImport', {newScenarioNode});

        // Save temporary data.
        processedScenarioObject[index] = newScenarioNode;

        let scenarioGroupType;
        if (scenarioItem.interactive_pause) {
          scenarioGroupType = ELEMENTS_TYPES.SMART_PAUSE;
        }
        if (scenarioItem.recognize_short_answer) {
          scenarioGroupType = ELEMENTS_TYPES.RECOGNIZE_ANSWER_GROUP_SHORT;
        }
        if (scenarioItem.recognize_long_answer) {
          scenarioGroupType = ELEMENTS_TYPES.RECOGNIZE_ANSWER_GROUP_LONG;
        }

        if (scenarioGroupType) {
          const childrenType = ELEMENTS_TYPES_GROUPS[scenarioGroupType];

          const newScenarioGroupNode = await context.dispatch('nodes/addNode', {
            isNew: true,
            from: newScenarioNode.id,
            type: scenarioGroupType,
            addDefaultChild: false,
          }, {root: true});
          let scenarioBehaviorNode = newScenarioGroupNode;

          if (scenarioItem.behavior) {
            for (const behavior of Object.values(scenarioItem.behavior)) {
              console.log('runImport', {behavior});

              if (childrenType) {
                scenarioBehaviorNode = await context.dispatch('nodes/addNode', {
                  isNew: true,
                  from: newScenarioGroupNode.id,
                  type: childrenType,
                  text: objectValue(behavior, 'keywords', null),
                }, {root: true});
              }

              let processedBehaviorActions = [];

              if (Array.isArray(behavior.actions)) {
                for (const [actionIndex, action] of behavior.actions.entries()) {
                  console.log('runImport', {action});

                  const prevActionIndex = actionIndex - 1;
                  const parentNode = prevActionIndex < 0
                    ? scenarioBehaviorNode
                    : processedBehaviorActions[prevActionIndex];

                  const getActionKey = (actionObject) => {
                    const type = Object.keys(actionObject)[0];
                    const values = Object.entries(ELEMENTS_TYPES_BRANCH_ACTIONS_KEYS)
                      .find(([key, value]) => value === type);
                    return values[0];
                  }
                  const type = getActionKey(action);

                  const getActionText = (actionObject) => Object.values(actionObject)[0];

                  const actionOptions = {
                    isNew: true,
                    from: parentNode.id,
                    type,
                  };

                  const text = getActionText(action);
                  if (text) {
                    actionOptions.text = text;
                  }

                  const newScenarioBehaviorActionNode = await context.dispatch('nodes/addNode', actionOptions, {root: true});

                  processedBehaviorActions.push(newScenarioBehaviorActionNode);
                }
              }

              if (behavior.after_action) {
                for (const [afterActionKey, afterActionValue] of Object.entries(behavior.after_action)) {
                  console.log('runImport', {afterActionKey, afterActionValue});

                  const parentNode = processedBehaviorActions.length > 0
                    ? processedBehaviorActions[processedBehaviorActions.length - 1]
                    : scenarioBehaviorNode;

                  if (afterActionKey === 'end') {
                    const nodeGoodbye = dataJSON[ELEMENTS_TYPES.GOODBYE];
                    await context.dispatch('nodes/addNode', {
                      isNew: true,
                      from: parentNode.id,
                      type: ELEMENTS_TYPES.GOODBYE,
                      text: objectValue(nodeGoodbye, 'message.text', null),
                    }, {root: true});
                  }

                  if (afterActionKey === 'goto' || afterActionKey === 'reaskfrom') {
                    const relatedToItem = processedScenarioObject[afterActionValue];
                    console.log('runImport', {relatedToItem});

                    if (relatedToItem) {
                      const aaOptions = {
                        isNew: true,
                        from: parentNode.id,
                        type: ELEMENTS_TYPES.LINK_TO_MESSAGE,
                        relatedToItemId: relatedToItem.id,
                      };
                      if (afterActionKey === 'reaskfrom') {
                        aaOptions.returnToParent = true;
                      }
                      await context.dispatch('nodes/addNode', aaOptions, {root: true});
                    } else {
                      await processScenario({
                        index: afterActionValue,
                        from: parentNode.id,
                      });
                    }
                  }
                }
              }
            }
          }
        }

        return newScenarioNode;
      }

      if (Object.entries(scenarioArray) && scenarioArray[0]) {
        await processScenario({
          index: 0,
          from: firstNode.id,
        });
      }
    }

    context.commit('updateField', {
      path: 'importDate',
      value: Date.now(),
    });

    context.commit('showJSON/reset', null, { root: true });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
